import React from 'react';
import { Button } from './ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from './ui/dropdown-menu';
import { ChevronUp, ChevronDown, ChevronDown as DropdownIcon } from 'lucide-react';
import { cn } from '../lib/utils';

export type OrderSortField = 'createdAt' | 'updatedAt' | 'total' | 'status' | 'paymentStatus' | 'customer';
export type OrderSortOrder = 'asc' | 'desc';

interface OrderSortProps {
  sortField: OrderSortField | null;
  sortOrder: OrderSortOrder;
  onSortChange: (field: OrderSortField | null, order: OrderSortOrder) => void;
  className?: string;
}

const SORT_OPTIONS: { value: OrderSortField; label: string }[] = [
  { value: 'createdAt', label: 'Order Date' },
  { value: 'updatedAt', label: 'Last Updated' },
  { value: 'total', label: 'Total Amount' },
  { value: 'status', label: 'Order Status' },
  { value: 'paymentStatus', label: 'Payment Status' },
  { value: 'customer', label: 'Customer Name' },
];

const OrderSort: React.FC<OrderSortProps> = ({
  sortField,
  sortOrder,
  onSortChange,
  className
}) => {
  const handleFieldChange = (value: string) => {
    if (value === 'none') {
      onSortChange(null, sortOrder);
    } else {
      onSortChange(value as OrderSortField, sortOrder);
    }
  };

  const handleOrderToggle = () => {
    const newOrder = sortOrder === 'asc' ? 'desc' : 'asc';
    onSortChange(sortField, newOrder);
  };

  const clearSort = () => {
    onSortChange(null, 'desc');
  };

  return (
    <div className={cn("flex items-center gap-2", className)}>
      <span className="text-sm font-medium text-gray-700 whitespace-nowrap">
        Sort by:
      </span>
      
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="outline" className="w-40 justify-between">
            <span className="truncate">
              {sortField ? SORT_OPTIONS.find(opt => opt.value === sortField)?.label : 'None'}
            </span>
            <DropdownIcon className="h-4 w-4 opacity-50" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="w-40">
          <DropdownMenuItem onClick={() => handleFieldChange('none')}>
            None
          </DropdownMenuItem>
          {SORT_OPTIONS.map((option) => (
            <DropdownMenuItem 
              key={option.value} 
              onClick={() => handleFieldChange(option.value)}
            >
              {option.label}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>

      {sortField && (
        <>
          <Button
            variant="outline"
            size="sm"
            onClick={handleOrderToggle}
            className="px-2"
            title={`Sort ${sortOrder === 'asc' ? 'ascending' : 'descending'}`}
          >
            {sortOrder === 'asc' ? (
              <ChevronUp className="h-4 w-4" />
            ) : (
              <ChevronDown className="h-4 w-4" />
            )}
          </Button>
          
          <Button
            variant="ghost"
            size="sm"
            onClick={clearSort}
            className="px-2 text-gray-500 hover:text-gray-700"
            title="Clear sorting"
          >
            Clear
          </Button>
        </>
      )}
    </div>
  );
};

export default OrderSort; 